import { BorderAlignmentTypes, CreativeTypes, ElementTypes, ShapeTypes, Template } from '@bynder-studio/render-core';
import { DesignConfig } from 'packages/hooks/useCreativeModelFactory';
import { Component, ComponentElement } from 'packages/services/componentService';
import {
    adjustElementPositions,
    calculateAspectRatio,
    calculateBoundingBox,
    createTransparentBackgroundElement,
} from 'packages/common/editor/presets/utils';

function createEmptyShape() {
    const color = {
        type: 'SOLID',
        brandColorId: null,
        red: 255,
        green: 255,
        blue: 255,
        opacity: 0,
    };

    return {
        id: 0,
        type: ElementTypes.SHAPE,
        name: 'Empty element',
        properties: {
            elementType: ElementTypes.SHAPE,
            shapeType: ShapeTypes.RECTANGLE,
            fillColor: color,
            borderColor: color,
            borderAlignment: BorderAlignmentTypes.INSIDE,
            opacity: 0,
            horizontalPosition: 0,
            verticalPosition: 0,
            width: 256,
            height: 256,
        },
    };
}

const createPosterFrame = (posterFrame: number) => ({
    id: Date.now() - 888 ** 2,
    type: ElementTypes.POSTER_FRAME,
    name: 'DEFAULT_POSTER_FRAME_NAME',
    properties: {
        elementType: ElementTypes.POSTER_FRAME,
        value: posterFrame,
        locked: true,
    },
});

const adjustElementAnimations = <T extends ComponentElement>(elements: T[]): T[] => {
    const adjustRecursively = (elementList: ComponentElement[]) => {
        Object.values(elementList).forEach((element) => {
            element.properties.animationIn = null;
            element.properties.animationOut = null;
            element.properties.animations = [];

            if (element.children) {
                adjustRecursively(Object.values(element.children));
            }
        });
    };

    adjustRecursively(elements);

    return elements;
};

export const detectDurationAndPosterFrame = (elements: ComponentElement[]) => {
    const { startFrame, endFrame } = elements.reduce(
        (acc, element) => {
            const elementStart = element.properties?.startFrame || 0;
            const elementEnd = elementStart + (element.properties?.duration || 1);

            return {
                startFrame: Math.min(acc.startFrame, elementStart),
                endFrame: Math.max(acc.endFrame, elementEnd),
            };
        },
        { startFrame: Infinity, endFrame: 0 },
    );

    const duration = endFrame || 1;
    const posterFrame = startFrame + Math.ceil((endFrame - startFrame) / 2);

    return {
        duration,
        posterFrame,
    };
};

export function buildDesignConfigFromComponent(component: Component): DesignConfig {
    const elements = component.elements.filter((el) => el.type !== ElementTypes.AUDIO);

    if (elements.length === 0) {
        elements.push(createEmptyShape());
    }

    const boundingBox = calculateBoundingBox(elements);
    const { width, height, minX, minY } = boundingBox;
    const aspectRatio = calculateAspectRatio(width, height);

    const adjustedElements = adjustElementAnimations(adjustElementPositions(elements, minX, minY));
    const { duration, posterFrame } = detectDurationAndPosterFrame(adjustedElements);

    if (component.type === CreativeTypes.VIDEO) {
        adjustedElements.push(createPosterFrame(posterFrame));
    }

    const page = {
        id: component.id,
        name: component.name,
        displayOrder: 0,
        width,
        height,
        aspectRatio,
        elements: [...adjustedElements, createTransparentBackgroundElement(component.id)],
        duration,
        frameRate: 25,
        shots: [],
    };

    const template = {
        status: 'ACTIVE',
        duration: 0,
        frameRate: 25,
        pages: [page],
        elements: component.elements,
        id: component.id,
        name: component.name,
    } as unknown as Template;

    return {
        template,
        textStyles: component.textStyles || [],
        contentProperties: component.contentProperties || [],
        galleryFonts: [],
    };
}
